import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    getPromAccounts,
    getPromCategoryMappings,
    getPromInternalCategories,
    getPromPlatformCategories,
    upsertPromCategoryMapping,
} from "../api/promApi";
import PromTopBar from "../components/marketplace/PromTopBar";
import "./IntegrationsPage.css";

// Категория считается «смапленной» по той же логике, что и в фиде:
// нужен platform_group_id и platform_category_id (или url). Иначе товары этой
// категории уходят в skipped (missing_prom_group/category_mapping).
const isCategoryMapped = (row) => Boolean(
    row.platform_group_id && (row.platform_category_id || row.platform_category_url)
);

const PromMappingsPage = () => {
    const location = useLocation();
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState(() => new URLSearchParams(location.search).get("account") || "");
    const [mappings, setMappings] = useState([]);
    const [internal, setInternal] = useState([]);
    const [unmapped, setUnmapped] = useState([]);
    const [platform, setPlatform] = useState([]);
    const [internalSearch, setInternalSearch] = useState("");
    const [platformSearch, setPlatformSearch] = useState("23005");
    const [draft, setDraft] = useState({ internal_category_code: "", platform_category_id: "23005" });
    const [message, setMessage] = useState("");

    const loadMappings = useCallback(async (id) => {
        if (!id) return;
        setMappings(await getPromCategoryMappings(id) || []);
    }, []);

    const loadUnmapped = useCallback(async (id) => {
        if (!id) return;
        const rows = await getPromInternalCategories(Number(id), { limit: 1000 }) || [];
        setUnmapped(rows.filter((row) => !isCategoryMapped(row)));
    }, []);

    useEffect(() => {
        getPromAccounts()
            .then((rows) => { setAccounts(rows || []); setAccountId((current) => current || String(rows?.[0]?.id || "")); })
            .catch(() => setMessage("Не удалось загрузить кабинеты Prom."));
    }, []);

    useEffect(() => { loadMappings(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить mapping категорий.")); }, [accountId, loadMappings]);

    useEffect(() => { loadUnmapped(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить категории без маппинга.")); }, [accountId, loadUnmapped]);

    useEffect(() => {
        if (!accountId) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const rows = await getPromInternalCategories(Number(accountId), { search: internalSearch || undefined, level: 2, limit: 100 });
                if (!cancelled) setInternal(rows || []);
            } catch (error) {
                if (!cancelled) setMessage(error?.response?.data?.detail || "Не удалось найти нашу категорию.");
            }
        }, 180);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [accountId, internalSearch]);

    useEffect(() => {
        if (!accountId) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const rows = await getPromPlatformCategories(Number(accountId), { search: platformSearch || undefined, limit: 100 });
                if (!cancelled) setPlatform(rows || []);
            } catch (error) {
                if (!cancelled) setMessage(error?.response?.data?.detail || "Не удалось найти категорию Prom.");
            }
        }, 180);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [accountId, platformSearch]);

    const save = async () => {
        await upsertPromCategoryMapping(Number(accountId), {
            internal_category_code: draft.internal_category_code,
            platform_category_id: draft.platform_category_id,
            is_active: true,
        });
        setMessage("Связь категорий сохранена.");
        await Promise.all([loadMappings(Number(accountId)), loadUnmapped(Number(accountId))]);
    };

    const pickUnmapped = (row) => {
        setDraft((current) => ({ ...current, internal_category_code: row.internal_category_code }));
        setInternalSearch(row.internal_category_name || row.internal_category_code);
        setMessage(`Выбрана категория «${row.internal_category_name || row.internal_category_code}» — укажите категорию Prom справа и нажмите «Связать».`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return <>
        <PromTopBar activeKey="mapping" accountId={accountId} localKeys={[]} onLocalSelect={() => {}} />
        {message && <div className="ip-status">{message}</div>}

        <section className="ip-card">
            <div className="ip-card-body">
                <h2 className="ip-title">Новая связь</h2>
                <p className="ip-lead">Ищите по фрагменту названия или ID, затем связывайте нашу категорию с категорией Prom.</p>
                <div className="ip-grid3" style={{ alignItems: "end" }}>
                    <label className="ip-fld"><span>Наша категория</span>
                        <input className="ip-inp" value={internalSearch} onChange={(e) => setInternalSearch(e.target.value)} placeholder="Например: вітамін, 2644" />
                        <select className="ip-inp" style={{ marginTop: "6px" }} value={draft.internal_category_code} onChange={(e) => setDraft({ ...draft, internal_category_code: e.target.value })}>
                            <option value="">Выберите из найденных ({internal.length})</option>
                            {internal.map((row) => <option key={row.internal_category_code} value={row.internal_category_code}>{row.internal_category_code} · {row.internal_category_name} · {row.products_count} товаров</option>)}
                        </select>
                    </label>
                    <label className="ip-fld"><span>Категория Prom</span>
                        <input className="ip-inp" value={platformSearch} onChange={(e) => setPlatformSearch(e.target.value)} placeholder="Например: біодобав, 23005" />
                        <select className="ip-inp" style={{ marginTop: "6px" }} value={draft.platform_category_id} onChange={(e) => setDraft({ ...draft, platform_category_id: e.target.value })}>
                            <option value="">Выберите из найденных ({platform.length})</option>
                            {platform.map((row) => <option key={row.category_id} value={row.category_id}>{row.category_id} · {row.category_name_ua}{row.category_path_ua && row.category_path_ua !== row.category_name_ua ? ` — ${row.category_path_ua}` : ""}</option>)}
                        </select>
                    </label>
                    <button type="button" className="ip-btn ip-btn-primary" disabled={!draft.internal_category_code || !draft.platform_category_id} onClick={() => save().catch((e) => setMessage(e?.response?.data?.detail || "Не удалось сохранить mapping."))}>Связать</button>
                </div>
                <p className="ip-lead" style={{ margin: 0 }}>Поиск работает по части слова: «добав» найдёт «Біодобавки», а «3005» — ID 23005.</p>
            </div>
        </section>

        <section className="ip-card">
            <div className="ip-card-body">
                <h2 className="ip-title">Категорії без маппінгу ({unmapped.length})</h2>
                <p className="ip-lead">Категории с товарами в наличии, у которых нет связи с Prom. Их товары не попадают в фид (skipped). Отсортированы по числу товаров — сверху самые важные.</p>
                {unmapped.length === 0
                    ? <div className="ip-callout good"><span className="ip-ic">✓</span><span>Все категории с товарами смаплены</span></div>
                    : <div className="ip-tbl-wrap">
                        <table className="ip-tbl">
                            <thead><tr><th>Наша категория</th><th style={{ textAlign: "right" }}>Товаров</th><th /></tr></thead>
                            <tbody>{unmapped.map((row) => <tr key={row.internal_category_code}>
                                <td>{row.internal_category_code} · {row.internal_category_name || ""}</td>
                                <td style={{ textAlign: "right", fontWeight: 700 }}>{row.products_count}</td>
                                <td style={{ textAlign: "right" }}><button type="button" className="ip-btn ip-btn-sm" onClick={() => pickUnmapped(row)}>Смапити →</button></td>
                            </tr>)}</tbody>
                        </table>
                    </div>}
            </div>
        </section>

        <section className="ip-card">
            <div className="ip-card-body">
                <h2 className="ip-title">Текущие связи ({mappings.length})</h2>
                <div className="ip-tbl-wrap">
                    <table className="ip-tbl">
                        <thead><tr><th>Наша категория</th><th>Категория Prom</th><th>Статус</th></tr></thead>
                        <tbody>{mappings.map((row) => <tr key={row.id}>
                            <td>{row.internal_category_code} · {row.internal_category_name || ""}</td>
                            <td>{row.platform_category_id} · {row.platform_category_name_ua || ""}</td>
                            <td><span className={`ip-pill ${row.is_active ? "g" : "off"}`}>{row.is_active ? "Активна" : "Отключена"}</span></td>
                        </tr>)}</tbody>
                    </table>
                </div>
            </div>
        </section>
    </>;
};

export default PromMappingsPage;
